// ============================================================
// TERROR — WILLIE'S WORKSHOP
// A walkable mini room. Same tile engine as Plaza + Dungeon1.
// No intro, no animation — you walk in, you're just there.
//
// Room layout (20 cols × 14 rows — cozy workshop):
//
//   ┌─────────────────────┐
//   │ wall  wall  wall    │  ← back wall (arch, shelves, fireplace)
//   │ shelf      fire  bk │  ← potions left, fire center, grimoire/crystal right
//   │ chest caul  ···  ·· │  ← chest, cauldron, open floor
//   │ ·····  circle  ···· │  ← summoning circle on floor (easter egg)
//   │ ·····  ·····  ····· │  ← player walks here
//   │──── EXIT SOUTH ─────│
//   └─────────────────────┘
//
// Interactables:
//   🔥 Fireplace (x:10,y:4) → The Forge
//   📖 Grimoire  (x:15,y:4) → The Codex
//   ⬛ Chest     (x:3, y:8) → Cheat Chest
//   🧑‍🔧 Willie   (x:8, y:7) → Speak with Willie
//   📚 The Dungeon (x:2,y:6) → Willee's Library (the W'ilys hate
//        to read, so the library got named "The Dungeon" — canon)
//   ✦ Circle    (x:10,y:10) → Secret (stand 8s)
//
// Cauldron / Crystal / Potion Shelves are BENCHED for now (their
// coming-soon bubbles crowded the room) — returning eventually.
//
// UI: all prompts + dialogue flow through engine/RoomConsole —
// the black bottom console. Dialogue lines persist in a session
// log; ◀ ▶ arrows cycle targets when 2+ are in range (Tab too).
// ============================================================


import { useState, useEffect, useRef, useCallback } from "react";
import { GM, facetTB, TEXT_SET, textGlow } from "./theme";
import {
  ZONE_BACKGROUNDS,
  PlayerEntity,
  MobileDpad, usePlayerAnimation } from "./engineIndex";
import RoomConsole, { getSessionLog, setSessionLog } from "./RoomConsole";
import RoomBackdrop from "./RoomBackdrop";
import { useEquippedCompanion } from "./useEquippedCompanion";
// ── THE FORGE, IN 3D (caP'n, 2026-09-22) — the Plaza's OWN switch. With it
// ON, Workshop3D paints this room in three dimensions from the same tile,
// facing and interactable refs; with it OFF this room is exactly the room
// that shipped. The only behavioural touch is rotateGridInput in the one
// movement loop, which is the identity whenever no 3D camera is mounted.
import Workshop3D from "./Workshop3D";
import { getPlaza3D, togglePlaza3D, subscribe as subscribePlazaCam, rotateGridInput } from "./plazaCamera";
import { getEquippedTitle } from "./CosmeticOverlay";

// GM now imports from theme.js v4 (Gilded Obsidian) — the local
// vellum-brown copy is DEAD. One palette, one source of truth.

// ── WORKSHOP KEYFRAMES — imperative injection (the <style>-block
// keyframe trap is closed in this file; theme.js Material Law 6). ──
function ensureWorkshopKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("ww-keyframes")) return;
  const el = document.createElement("style");
  el.id = "ww-keyframes";
  el.textContent = `
    @keyframes workshopPulse { 0%,100%{opacity:0.6} 50%{opacity:1} }
    @keyframes bubbleFloat { from{transform:translateX(-50%) translateY(0px)} to{transform:translateX(-50%) translateY(-5px)} }
    @media (prefers-reduced-motion: reduce) {
      [style*="workshopPulse"] { animation: none !important; }
    }
  `;
  document.head.appendChild(el);
}
ensureWorkshopKeyframes();


// ─────────────────────────────────────────────────────────────
// ZONE CONSTANTS — Workshop specific
// ─────────────────────────────────────────────────────────────
const COLS  = 20;
const ROWS  = 14;
const SPAWN = { x: 9, y: 9 };

const FORGE_BG = ZONE_BACKGROUNDS.workshop;
// ⚠ THE POINTER LAW (2026-08-22) — TOUCH ALONE IS NOT "MOBILE".
// `navigator.maxTouchPoints > 0` is true on every touchscreen laptop and
// 2-in-1, so this said "phone" on caP'n's PC. In OrientationLock it was a
// hard lockout (the rotate curtain over a monitor that cannot rotate);
// here it is thumb controls and phone layout handed to someone with a
// mouse. `(pointer: coarse)` asks about the PRIMARY pointer instead of
// what the hardware merely supports: phone/tablet coarse, touchscreen PC
// fine. Touch is still required, so mouse-only desktops are unchanged.
// Full reasoning lives once, on isMobile() in OrientationLock.js.
const isMobile = () => {
  if (typeof window === "undefined") return false;
  if (!(("ontouchstart" in window) || (navigator.maxTouchPoints || 0) > 0)) return false;
  try {
    if (typeof window.matchMedia === "function") return window.matchMedia("(pointer: coarse)").matches;
  } catch { /* fall back to the touch-only answer rather than throw */ }
  return true;
};

// Workshop fits the whole room on screen — no scrolling needed
function calcTile() {
  return Math.min(
    Math.floor(window.innerWidth  / COLS),
    Math.floor(window.innerHeight / ROWS)
  );
}

function calcCamOffset(px, py, tile) {
  const vw = window.innerWidth, vh = window.innerHeight;
  const mapW = COLS * tile, mapH = ROWS * tile;
  const rawX = px * tile + tile / 2 - vw / 2;
  const rawY = py * tile + tile / 2 - vh / 2;
  return {
    x: Math.max(0, Math.min(Math.max(0, mapW - vw), rawX)),
    y: Math.max(0, Math.min(Math.max(0, mapH - vh), rawY)),
  };
}

// ─────────────────────────────────────────────────────────────
// MAP
// ─────────────────────────────────────────────────────────────
const T = { FLOOR: 0, WALL: 1 };

function buildWorkshopMap() {
  const map = Array.from({ length: ROWS }, () => Array(COLS).fill(T.FLOOR));
  for (let x = 0; x < COLS; x++) { map[0][x] = T.WALL; map[ROWS-1][x] = T.WALL; }
  for (let y = 0; y < ROWS; y++) { map[y][0] = T.WALL; map[y][COLS-1] = T.WALL; }
  for (let y = 1; y <= 4; y++)
    for (let x = 1; x <= COLS-2; x++)
      map[y][x] = T.WALL;
  for (let y = 5; y <= 6; y++)
    for (let x = 3; x <= 12; x++)
      map[y][x] = T.WALL;
  for (let y = 4; y <= 10; y++) {
    map[y][16] = T.WALL; map[y][17] = T.WALL; map[y][18] = T.WALL;
  }
  for (let y = 4; y <= 9; y++) {
    map[y][1] = T.FLOOR; map[y][2] = T.FLOOR;
  }
  return map;
}

const WORKSHOP_MAP = buildWorkshopMap();

function isWalkable(x, y) {
  if (x < 0 || y < 0 || x >= COLS || y >= ROWS) return false;
  return WORKSHOP_MAP[y][x] === T.FLOOR;
}

// ─────────────────────────────────────────────────────────────
// INTERACTABLES
// ─────────────────────────────────────────────────────────────
const INTERACTABLES = [
  { id: "fireplace",    x: 10, y: 4,  radius: 3, verb: "Forge",   label: "The Forge",   color: "#f97316",        action: "forge"      },
  { id: "grimoire",     x: 15, y: 4,  radius: 3, verb: "Study",   label: "The Codex",   color: GM.inkGold,       action: "codex"      },
  { id: "chest",        x: 3,  y: 8,  radius: 2, verb: "Open",    label: "Cheat Chest", color: GM.inkGoldBright, action: "cheatChest" },
  { id: "willie",       x: 8,  y: 7,  radius: 2, verb: "Speak",   label: "Willie",      color: "#c9a84c",        action: "willie"     },
  // The left-wall door. It leads to Willee's Library. The W'ilys
  // hate to read, so they named the library "The Dungeon." Canon.
  { id: "dungeon_door", x: 2,  y: 6,  radius: 3, verb: "Descend", label: "The Dungeon", color: "#6d28d9",        action: "library"    },
  { id: "circle",       x: 10, y: 10, radius: 2, verb: "Study",   label: "Summoning Circle", color: "#22d3ee44", action: "circle",    secret: true },
];

// BENCHED (coming back eventually — pulled to unclutter the room):
//   { id: "cauldron", x: 6,  y: 4, ... }   Brew — The Cauldron
//   { id: "crystal",  x: 13, y: 9, ... }   Peer — The Crystal
//   { id: "shelves",  x: 1,  y: 6, ... }   Browse — Potion Shelves

// ─────────────────────────────────────────────────────────────
// DIALOGUE LINES — Workshop specific
// ─────────────────────────────────────────────────────────────
const WILLIE_LINES = [
  "◆ Ah. You found it.",
  "Willie's Workshop. My workshop. I am Willie.",
  "Not the same Willie from outside. Different Willie.",
  "...The broom is different too. Please don't check.",
];

const CIRCLE_LINES = [
  "The circle hums beneath your feet.",
  "You've been standing here a while.",
  "Something is paying attention.",
  "◆ ...",
];

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
export default function WilliesWorkshop({
  profile,
  onExit,
  onForge,
  onCodex,
  onCheatChest,
  onEnterDungeon,
  onLibrary,
}) {
  const gender = profile?.gender || "f";
  const mobile = isMobile();
  const companion = useEquippedCompanion(profile?.uid);

  // ── Tile + camera ──────────────────────────────────────────
  const [tile, setTile]           = useState(calcTile);
  const [camOffset, setCamOffset] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const update = () => setTile(calcTile());
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);
  const TILE = tile;

  // ── Player state ───────────────────────────────────────────
  const [pos, setPos]             = useState(SPAWN);
  const [facing, setFacing]       = useState({ x: 0, y: -1 });
  const [walkFrame, setWalkFrame] = useState(false);
  // Rest + true-idle come from the engine hook; walkFrame stays local
  // because this screen drives it from its own movement loop.
  const { motion, idleStep } = usePlayerAnimation(pos);
  const walkRef   = useRef(false);
  const posRef    = useRef(SPAWN);
  const keysRef   = useRef({});
  const menuRef   = useRef(false);
  const facingRef = useRef({ x: 0, y: -1 });   // 3D: the body's facing, read in useFrame
  const selRef    = useRef(null);              // 3D: which ◆ the console has selected

  // ── The 3D switch — the Plaza's, shared and persisted ──────
  const [plaza3D, setPlaza3DState] = useState(getPlaza3D);
  useEffect(() => subscribePlazaCam(() => setPlaza3DState(getPlaza3D())), []);

  // ── Interaction state ──────────────────────────────────────
  const [nearbyList, setNearbyList] = useState([]);   // all in range
  const [nearbyIdx, setNearbyIdx]   = useState(0);    // ◀ ▶ selection
  const [dialogue, setDialogue]     = useState(null); // {lines,color,label,idx}
  const circleTimer = useRef(null);
  const nearbyIdsRef = useRef("");

  // ── Session log — persists for the whole browser session ───
  const [log, setLog] = useState(() => getSessionLog("workshop").slice());
  const addLog = useCallback((entry) => {
    setLog(prev => {
      const next = [...prev, entry];
      setSessionLog("workshop", next);
      return next;
    });
  }, []);

  // ── Dialogue flow — every line lands in the session log ────
  const startDialogue = useCallback((lines, color, label) => {
    menuRef.current = true;
    addLog({ label, color, text: lines[0] });
    setDialogue({ lines, color, label, idx: 0 });
  }, [addLog]);

  const advanceDialogue = useCallback(() => {
    if (!dialogue) return;
    const next = dialogue.idx + 1;
    if (next >= dialogue.lines.length) {
      setDialogue(null);
      menuRef.current = false;
      return;
    }
    addLog({ label: dialogue.label, color: dialogue.color, text: dialogue.lines[next] });
    setDialogue({ ...dialogue, idx: next });
  }, [dialogue, addLog]);

  useEffect(() => { selRef.current = nearbyList[nearbyIdx]?.id || null; }, [nearbyList, nearbyIdx]);

  const cycleNearby = useCallback((dir) => {
    setNearbyIdx(i => {
      const n = nearbyList.length;
      return n ? (i + dir + n) % n : 0;
    });
  }, [nearbyList.length]);

  // ── Camera follow ──────────────────────────────────────────
  useEffect(() => {
    setCamOffset(calcCamOffset(pos.x, pos.y, TILE));
  }, [pos, TILE]);

  // ── Nearby detection ──────────────────────────────────────
  useEffect(() => {
    if (menuRef.current) return;
    const found = INTERACTABLES.filter(obj => {
      if (obj.secret) return false;
      return Math.abs(pos.x - obj.x) <= obj.radius &&
             Math.abs(pos.y - obj.y) <= obj.radius;
    });
    const ids = found.map(f => f.id).join(",");
    if (ids !== nearbyIdsRef.current) {
      nearbyIdsRef.current = ids;
      setNearbyList(found);
      setNearbyIdx(0); // reset selection when the in-range set changes
    }

    // Circle easter egg — stand on it 8 seconds
    const onCircle = Math.abs(pos.x - 9) <= 1.5 && Math.abs(pos.y - 9) <= 1.5;
    if (onCircle) {
      if (!circleTimer.current) {
        circleTimer.current = setTimeout(() => {
          startDialogue(CIRCLE_LINES, "#22d3ee", "Summoning Circle");
        }, 8000);
      }
    } else {
      clearTimeout(circleTimer.current);
      circleTimer.current = null;
    }
  }, [pos, startDialogue]);

  // ── Handle action ──────────────────────────────────────────
  const handleAction = useCallback((obj) => {
    if (!obj) return;

    if (obj.comingSoon) {
      startDialogue(
        [`${obj.label} stirs.`, "Something is being prepared here.", "◆ Coming soon."],
        obj.color, obj.label
      );
      return;
    }

    switch (obj.action) {
      case "forge":      onForge && onForge(profile); break;
      case "codex":      onCodex && onCodex(profile); break;
      case "cheatChest": onCheatChest && onCheatChest(); break;
      case "willie":     startDialogue(WILLIE_LINES, "#c9a84c", "Willie"); break;
      case "library":    onLibrary && onLibrary(profile); break;
      default: break;
    }
  }, [profile, onForge, onCodex, onCheatChest, onLibrary, startDialogue]);

  // ── Keyboard ───────────────────────────────────────────────
  useEffect(() => {
    const down = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (dialogue) { setDialogue(null); menuRef.current = false; }
        else onExit && onExit();
        return;
      }
      // Dialogue open — Q / Enter / Space advance it
      if (dialogue) {
        if (e.key === "q" || e.key === "Q" || e.key === "Enter" || e.key === " ") {
          e.preventDefault(); advanceDialogue();
        }
        return;
      }
      // Tab cycles between multiple in-range targets (matches ◀ ▶)
      if (e.key === "Tab" && nearbyList.length > 1) {
        e.preventDefault(); cycleNearby(1); return;
      }
      if ((e.key === "q" || e.key === "Q") && nearbyList.length > 0) {
        e.preventDefault(); handleAction(nearbyList[nearbyIdx]); return;
      }
      if (!menuRef.current) keysRef.current[e.key] = true;
    };
    const up    = (e) => { keysRef.current[e.key] = false; };
    const clear = ()  => { keysRef.current = {}; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup",   up);
    window.addEventListener("blur",    clear);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup",   up);
      window.removeEventListener("blur",    clear);
    };
  }, [nearbyList, nearbyIdx, dialogue, handleAction, advanceDialogue, cycleNearby, onExit]);

  // ── Movement loop ──────────────────────────────────────────
  useEffect(() => {
    let lastMove = 0;
    const loop = setInterval(() => {
      if (menuRef.current) { keysRef.current = {}; return; }
      const now = Date.now();
      if (now - lastMove < 130) return;
      const k = keysRef.current;
      let dx = 0, dy = 0;
      if (k["ArrowUp"]    || k["w"] || k["W"]) dy = -1;
      if (k["ArrowDown"]  || k["s"] || k["S"]) dy =  1;
      if (k["ArrowLeft"]  || k["a"] || k["A"]) dx = -1;
      if (k["ArrowRight"] || k["d"] || k["D"]) dx =  1;
      if (dx === 0 && dy === 0) return;
      // W means "away from the camera" in 3D; the identity in 2D (plazaCamera.js)
      ({ x: dx, y: dy } = rotateGridInput(dx, dy));
      lastMove = now;
      setPos(prev => {
        const nx = prev.x + dx, ny = prev.y + dy;
        if (ny >= ROWS) { onExit && onExit(); return prev; }
        if (!isWalkable(nx, ny)) return prev;
        posRef.current = { x: nx, y: ny };
        facingRef.current = { x: dx, y: dy };
        setFacing({ x: dx, y: dy });
        walkRef.current = !walkRef.current;
        setWalkFrame(walkRef.current);
        return { x: nx, y: ny };
      });
    }, 50);
    return () => clearInterval(loop);
  }, [onExit]);

  // ── Render ─────────────────────────────────────────────────
  return (
    <div style={{ position: "fixed", inset: 0, overflow: "hidden",
      background: "transparent", fontFamily: GM.font }}>
      {/* LETTERBOX BACKDROP — equipped in the Backdrop Atelier.
          Root + frame are transparent so it shows around the map. */}
      <RoomBackdrop uid={profile?.uid} />
      {/* THE FORGE IN 3D — stands in for the painted map block when the switch is on */}
      {plaza3D && (
        <Workshop3D
          tileRef={posRef}
          facingRef={facingRef}
          selRef={selRef}
          interactables={INTERACTABLES}
          gender={gender}
          playerName={profile?.name || "Aenarchist"}
          title={getEquippedTitle(profile?.equipped)}
          companionId={companion.companionId || null}
          radiantTier={companion.radiantTier || null}
          mobile={mobile}
        />
      )}
      {/* MAP */}
      {!plaza3D && <div style={{ position: "absolute", inset: 0, overflow: "hidden",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "transparent" }}>
        <div style={{ position: "relative", width: COLS * TILE, height: ROWS * TILE, flexShrink: 0 }}>

          {/* Background */}
          <img
            src={FORGE_BG}
            alt="Willie's Workshop"
            onError={e => { e.target.style.display="none"; e.target.parentNode.style.background="linear-gradient(160deg,#0a0414,#130a2a,#0d0820)"; }}
            style={{ position: "absolute", left: 0, top: 0, width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "center top", zIndex: 0 }}
          />

          {/* Summoning circle glow */}
          <div style={{
            position: "absolute", left: 7 * TILE, top: 8 * TILE,
            width: 3 * TILE, height: 2 * TILE,
            background: "radial-gradient(ellipse at center, rgba(34,211,238,0.12), transparent 70%)",
            zIndex: 2, pointerEvents: "none",
            animation: "workshopPulse 3s ease-in-out infinite",
          }} />

          {/* "The Dungeon" arch glow — the door down to Willee's Library */}
          <div style={{
            position: "absolute", left: 0, top: 4 * TILE,
            width: 3 * TILE, height: 5 * TILE,
            background: "radial-gradient(ellipse at 80% 40%, rgba(109,40,217,0.25), transparent 70%)",
            zIndex: 2, pointerEvents: "none",
            animation: "workshopPulse 4s ease-in-out infinite",
          }} />


          {/* Interactable markers */}
          {INTERACTABLES.filter(o => !o.secret).map(obj => (
            <div key={obj.id} style={{
              position: "absolute", left: obj.x * TILE, top: obj.y * TILE,
              zIndex: 2, pointerEvents: "none", fontSize: 7,
              color: `${obj.color}66`, fontFamily: GM.font,
              letterSpacing: 1, textShadow: `0 0 4px ${obj.color}`, whiteSpace: "nowrap",
            }}>{obj.comingSoon ? "·" : "◆"}</div>
          ))}

          {/* Player + Companion */}
          <PlayerEntity
            pos={pos}
            facing={facing}
            gender={gender}
            walkFrame={walkFrame}
            motion={motion} idleStep={idleStep}
            tile={TILE}
            companionId={companion.companionId}
            radiantTier={companion.radiantTier}
            showCompanion={true}
          />

        </div>
      </div>}

      {/* HUD */}
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
        padding: mobile ? "8px 10px" : "10px 16px",
        display: "flex", alignItems: "center", gap: 10,
        background: "linear-gradient(180deg, rgba(7,4,16,0.92), transparent)",
        pointerEvents: "none",
      }}>
        <button onClick={onExit} style={{
          pointerEvents: "all",
          background: "linear-gradient(175deg, #1e1636 0%, #150e26 55%, #0c0818 100%)",
          border: "none", borderTop: "1px solid rgba(226,232,244,0.2)",
          clipPath: facetTB(7),
          boxShadow: "inset 0 1px 0 rgba(226,232,244,0.14), inset 0 -3px 6px rgba(0,0,0,0.4)",
          filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.55))",
          color: GM.vellumDim,
          padding: mobile ? "5px 10px" : "6px 14px",
          fontSize: 10, cursor: "pointer",
          fontFamily: GM.font, letterSpacing: 1, textShadow: TEXT_SET,
        }}>← Exit</button>
        <div style={{
          fontSize: mobile ? 9 : 10, color: "#f0cc60",
          letterSpacing: 3, fontStyle: "italic", fontFamily: GM.font,
          textShadow: textGlow(GM.inkGoldBright),
        }}>◆ Willie's Workshop</div>
        {/* ⬡ THE 3D SWITCH — the same button, and the same switch, as the Plaza */}
        <button onClick={togglePlaza3D} title="Toggle the 3D Forge" style={{
          pointerEvents: "all",
          padding: "2px 8px", borderRadius: 4, cursor: "pointer",
          background: plaza3D ? `linear-gradient(135deg, ${GM.inkGold}2a, transparent)` : "transparent",
          border: `1px solid ${plaza3D ? GM.inkGold : GM.inkGold + "44"}`,
          color: plaza3D ? GM.inkGoldBright : GM.vellumDim,
          fontFamily: GM.font, fontSize: 9.5, letterSpacing: 1.5, fontWeight: 700,
        }}>{plaza3D ? "⬡ 3D" : "▤ 2D"}</button>
      </div>

      {/* Room Console — prompts, dialogue, and the session log */}
      <RoomConsole
        mobile={mobile}
        zoneId="workshop"
        topOffset={116}
        log={log}
        dialogue={dialogue}
        onAdvance={advanceDialogue}
        nearbyList={nearbyList}
        nearbyIndex={nearbyIdx}
        onCycle={cycleNearby}
        onActivate={() => handleAction(nearbyList[nearbyIdx])}
      />

      {/* Mobile D-Pad */}
      {mobile && <MobileDpad keysRef={keysRef} />}

      {/* Controls hint — only while the console is hidden */}
      {!mobile && !dialogue && nearbyList.length === 0 && log.length === 0 && (
        <div style={{
          position: "fixed", bottom: 12, left: "50%", transform: "translateX(-50%)",
          fontSize: 8, color: GM.vellumFaint, letterSpacing: 3,
          zIndex: 50, pointerEvents: "none",
          background: "linear-gradient(175deg, #05030c 0%, #0d0918 60%, #120c20 100%)",
          border: `1px solid ${GM.pageBorder}`,
          boxShadow: "inset 0 2px 8px rgba(0,0,0,0.75)",
          padding: "5px 14px", borderRadius: 8, textShadow: TEXT_SET,
        }}>WASD · Q TO INTERACT · TAB TO CYCLE · ESC TO EXIT</div>
      )}
    </div>
  );
}